/**
 * Компенсация разницы в метриках контейнера между PSD и рендерингом.
 * При парсинге PSD контейнер получается чуть уже, чем нужно для текста,
 * из-за чего автоперенос срабатывает раньше, чем в оригинальном дизайне.
 * Добавляем к `containerWidth` при расчёте `wrapWidth`.
 *
 * Единый источник истины — менять только здесь.
 */
export const PSD_CONTAINER_TOLERANCE_PX = 5;

/** `textLayout` from which block text wraps by Photoshop's rule: the line's advance against the exact frame. */
export const TEXT_LAYOUT_EXACT_WRAP = 1;

/** `textLayout` from which a point-text line ends at its pen: trailing spaces count toward right/centre alignment. */
export const TEXT_LAYOUT_PEN_LINES = 2;

/** `textLayout` from which every style run draws with its own font, not the box font. */
export const TEXT_LAYOUT_RUN_FONTS = 2;

/** `textLayout` from which Faux Bold widens every advance and strokes by the on-screen size, as Photoshop does. */
export const TEXT_LAYOUT_FAUX_BOLD = 3;

/** `textLayout` from which Photoshop's Optical kerning (`kerning` -1) spaces pairs from the outlines, not the font's metrics. */
export const TEXT_LAYOUT_OPTICAL_KERN = 3;

/**
 * `textLayout` from which each line steps down by the largest leading of its runs (`lineHeightPx`), and a paragraph
 * break adds the box's `spaceAfter` + `spaceBefore` (canvas px) — Photoshop's line stack.
 */
export const TEXT_LAYOUT_LINE_STACK = 4;

/** `textLayout` from which area text seats its first baseline at `firstBaselineOffset` when the import pinned it. */
export const TEXT_LAYOUT_FRAME_BASELINE = 5;

/** `textLayout` from which a word of a box stamped with `language` hyphenates by its run's Photoshop language. */
export const TEXT_LAYOUT_RUN_LANGUAGE = 6;

/**
 * `textLayout` from which text keeps Photoshop's paragraphs: a soft return ({@link SOFT_LINE_BREAK}) ends a line inside
 * its paragraph (no paragraph spacing, no first-line indent), each paragraph takes its own indents, spacing and
 * alignment from `paragraphRuns`, and a line of several runs wraps by the font and size of each run.
 */
export const TEXT_LAYOUT_PARAGRAPHS = 7;

/**
 * `textLayout` from which block text hides the lines that do not fit its frame, as InDesign hides overset text: a line
 * whose baseline falls below the frame bottom, and every line after it, draws nothing (a first line already below it
 * leaves the frame empty). The model keeps the whole text. The clip belongs to this step alone: unlike the rest of the
 * ladder it is not cumulative, so a later step (such as {@link TEXT_LAYOUT_INDENT_BAND}) does not inherit it, and the
 * next InDesign step must turn the clip on explicitly. Stamped by the InDesign import only. Only the Rust layout clips:
 * the TS layout twin (`glyph-layout-backend.ts`: psd-render-lab, the inline-edit fit clamp, the overflow badge, the area
 * caret) does not know this step and lays out every line.
 */
export const TEXT_LAYOUT_FRAME_CLIP = 8;

/**
 * `textLayout` from which a right or centre aligned line sits in its paragraph's band between the indents, as justified
 * lines already do: the band is the line's frame less `startIndent`, `endIndent` and, on a paragraph's first line,
 * `firstLineIndent`. A right line ends at `endIndent` from the frame edge, a centre line centres in the band. A line
 * wider than an indented band starts at its start indent, as a left line does. Not checked against Photoshop.
 */
export const TEXT_LAYOUT_INDENT_BAND = 9;

/**
 * `textLayout` from which point text with whole glyph widths (Photoshop `UseFractionalGlyphWidths` off) sets on
 * Photoshop's pixel grid: every glyph steps a whole pixel of the typed size, scaled by the text matrix, its origin
 * sits on a whole document pixel, the line hangs off the matrix origin by its alignment and the first baseline sits
 * on the matrix origin's row. Checked against Photoshop's raster for untracked, unscaled, unrotated point text; any
 * other box lays out as at {@link TEXT_LAYOUT_INDENT_BAND}. Only the Rust layout knows this step: the TS layout twin
 * (`glyph-layout-backend.ts`: psd-render-lab, the inline-edit fit clamp, the overflow badge, the area caret) keeps the
 * step-below layout.
 */
export const TEXT_LAYOUT_GLYPH_GRID = 10;

/**
 * `textLayout` from which Cyrillic letters of point and area text shape without the font's standard ligatures
 * (`liga`), as Photoshop sets them: each letter keeps its own advance, so on the glyph grid each rounds its own step.
 * The rule follows the letters, not the string: Latin and other scripts keep their `liga` in the same line. An explicit
 * `ligatures: false` still turns them all off; vertical text and text on a path keep their ligatures (Photoshop's rule
 * there is not measured). Only the Rust layout knows this step. The TS layout twin (`glyph-layout-backend.ts`) keeps
 * the step-below layout: in psd-render-lab, and also where it answers the inline-edit fit
 * clamp, the overflow badge and the area caret, so these still see Cyrillic `liga`. Off the glyph grid the caret is
 * the browser's and ligates too.
 */
export const TEXT_LAYOUT_CYRILLIC_LIGATURES = 11;

/**
 * `textLayout` from which tracked point text takes {@link TEXT_LAYOUT_GLYPH_GRID}'s pixel grid too, in the case
 * checked against Photoshop's raster: one line, left or centre aligned, one tracking for the box and every run, typed
 * at 64 px or larger. Each glyph steps its advance plus tracking rounded to a whole typed pixel; the line aligns by
 * its width without the tracking after its last glyph, and a centred line starts half the first glyph's tracking to
 * the right. Any other tracked box lays out as at {@link TEXT_LAYOUT_GLYPH_GRID}; untracked boxes lay out as there.
 * Rust layout only, as the step below.
 */
export const TEXT_LAYOUT_GLYPH_GRID_TRACKING = 12;

/**
 * `textLayout` from which area text with whole glyph widths sets on the same pixel grid as point text at
 * {@link TEXT_LAYOUT_GLYPH_GRID}, anchored by its frame: each line hangs in the frame by the sum of its whole typed-px
 * steps (the measure it wraps by too, so a line breaks where Photoshop breaks it), and its baseline steps off the
 * unrounded frame top. Checked against Photoshop's raster for top-aligned, left or centred, untracked, unindented area
 * text whose matrix scales 1 and up evenly on both axes, with no contour and no page object in the frame; any other
 * box lays out as at the step below; tracked area text too, as {@link TEXT_LAYOUT_GLYPH_GRID_TRACKING} grids tracked
 * point text only. It is the first grid step that moves line breaks. Only the Rust layout knows this step. The TS
 * layout twin (`glyph-layout-backend.ts`) keeps the step-below layout with fractional widths in psd-render-lab; the
 * inline-edit fit clamp and the overflow badge take the line count of such a box from the engine's caret grid
 * (`engineFrameFit`), and its caret is the engine's, so they see the lines the canvas draws. The canvas and the print
 * draw by the Rust layout, so no drawn pixel follows the twin.
 */
export const TEXT_LAYOUT_GLYPH_GRID_AREA = 13;

/** Photoshop's soft return (Shift+Enter) in the model: U+2028 LINE SEPARATOR — one code unit, as `\x03` in the PSD. */
export const SOFT_LINE_BREAK = '\u2028';

/** Splits authored text into its lines from `TEXT_LAYOUT_PARAGRAPHS`: at a paragraph break or a soft return. */
export const PARAGRAPH_OR_SOFT_BREAK = new RegExp(`[\n${SOFT_LINE_BREAK}]`, 'u');

/**
 * The `textLayout` a PSD import stamps on its text boxes — the newest Photoshop text rule. It is past
 * {@link TEXT_LAYOUT_FRAME_CLIP} and does not clip: that step's clip is not inherited by the steps above it.
 */
export const PSD_TEXT_LAYOUT = TEXT_LAYOUT_GLYPH_GRID_AREA;

/** A box stamped with `textLayout` at or past `rule` (absent — legacy, below every rule). */
export function textLayoutAtLeast(textLayout: number | undefined, rule: number): boolean {
  return (textLayout ?? 0) >= rule;
}

// Float noise of `boxBounds × matrix` (876.9999999999998), not slack: a word exactly as wide as its frame stays on it.
const EXACT_WRAP_EPSILON_PX = 0.01;

/** Photoshop's default minimum word spacing (Justification panel): a justified line may close its spaces to it. */
export const PSD_MIN_WORD_SPACING = 0.8;

/** Wrap-budget slack over the frame width for a box stamped with `textLayout` (absent — the legacy rule). */
export function wrapSlackPx(textLayout: number | undefined): number {
  return (textLayout ?? 0) >= TEXT_LAYOUT_EXACT_WRAP ? EXACT_WRAP_EPSILON_PX : PSD_CONTAINER_TOLERANCE_PX;
}

/** Share of a word space a justified line may give up to stay on its line; `0` = the legacy rule (no squeeze). */
export function justifySqueezeRatio(textLayout: number | undefined): number {
  return (textLayout ?? 0) >= TEXT_LAYOUT_EXACT_WRAP ? 1 - PSD_MIN_WORD_SPACING : 0;
}
